"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@chakra-ui/react";
import { useRouter } from "next/navigation";
import { toaster } from "@/components/ui/toaster";
import Map from "./map";
import ReportHazards from "./report-hazards";
import MobileReportHazards from "./mobile-report-hazards";
import { FeatureCollection, Geometry } from "geojson";
import HomeHeader from "./home-header";
import { useSearchParams } from "next/navigation";
import { useHazardDataFetcher } from "../hooks/useHazardDataFetcher";
const addressLookupCoordinates = {
  geometry: {
    type: "Point",
    coordinates: [-122.408020683, 37.801698301]
  }
};
const defaultCoords = addressLookupCoordinates.geometry.coordinates ?? [];
const toggledStatesDefaults = [true, true, true];
interface AddressMapperProps {
  softStoryData: FeatureCollection<Geometry>;
  tsunamiData: FeatureCollection<Geometry>;
  liquefactionData: FeatureCollection<Geometry>;
}
export type LayerToggleObjProps = {
  layerId: string;
  toggleState: boolean;
};
type ErrorResult = {
  error: true;
  message: string;
};
const isErrorResult = (data: unknown): data is ErrorResult => {
  return typeof data === "object" && data !== null && "error" in data && (data as any).error === true;
};
const AddressMapper: React.FC<AddressMapperProps> = t0 => {
  const $ = _c(56);
  const {
    softStoryData,
    tsunamiData,
    liquefactionData
  } = t0;
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get("lat");
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const initialLat = t1;
  let t2;
  if ($[2] !== searchParams) {
    t2 = searchParams.get("lon");
    $[2] = searchParams;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const initialLon = t2;
  let t3;
  if ($[4] !== searchParams) {
    t3 = searchParams.get("address");
    $[4] = searchParams;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const initialAddress = t3;
  let t4;
  if ($[6] !== initialLat || $[7] !== initialLon) {
    t4 = initialLat && initialLon ? [parseFloat(initialLon), parseFloat(initialLat)] : null;
    $[6] = initialLat;
    $[7] = initialLon;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const [coordinates, setCoordinates] = useState(t4);
  const [searchedAddress, setSearchedAddress] = useState(initialAddress || null);
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {};
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const [addressHazardData, setAddressHazardData] = useState(t5);
  const [isHazardDataLoading, setHazardDataLoading] = useState(false);
  const [toggledStates, setToggledStates] = useState(toggledStatesDefaults);
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      layerId: "",
      toggleState: true
    };
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const [layerToggleObj, setLayerToggleObj] = useState(t6);
  const [showHazards, setShowHazards] = useState(false);
  const [isSearchComplete, setSearchComplete] = useState(false);
  const [currentView, setCurrentView] = useState("");
  const coordinatesRef = useRef(null);
  const router = useRouter();
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      setSearchComplete,
      setHazardDataLoading
    };
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const {
    fetchHazardData
  } = useHazardDataFetcher(t7);
  let t8;
  if ($[12] !== fetchHazardData) {
    t8 = async coords => {
      ;
      try {
        const values = await fetchHazardData(coords);
        setAddressHazardData(values);
      } catch (t9) {
        const error = t9;
        console.error("Error while retrieving data: ", error instanceof Error ? error.message : error?.toString());
        setAddressHazardData({
          softStory: null,
          tsunami: null,
          liquefaction: null
        });
        toaster.create({
          description: "Could not retrieve hazard data",
          type: "error",
          duration: 5000,
          closable: true
        });
      }
    };
    $[12] = fetchHazardData;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const updateHazardData = t8;
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setCurrentView(window.innerWidth <= 480 ? "mobile" : "desktop");
    };
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const handleResize = t9;
  let t10;
  if ($[15] !== router) {
    t10 = (coords_0, address) => {
      const newUrl = `?address=${encodeURIComponent(address)}&lat=${coords_0[1]}&lon=${coords_0[0]}`;
      router.push(newUrl, {
        scroll: false
      });
    };
    $[15] = router;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const handleSearchChange = t10;
  let t11;
  let t12;
  if ($[17] !== searchParams || $[18] !== updateHazardData) {
    t11 = () => {
      const lat = searchParams.get("lat");
      const lon = searchParams.get("lon");
      const address_0 = searchParams.get("address");
      if (lat && lon && address_0) {
        const newCoords = [parseFloat(lon), parseFloat(lat)];
        const lastCoords = coordinatesRef.current;
        if (!lastCoords || lastCoords[0] !== newCoords[0] || lastCoords[1] !== newCoords[1]) {
          setCoordinates(newCoords);
          setSearchedAddress(address_0);
          coordinatesRef.current = newCoords;
          updateHazardData(newCoords);
        }
      } else {
        if (coordinatesRef.current) {
          setCoordinates(null);
          setSearchedAddress(null);
          setAddressHazardData({});
          setSearchComplete(false);
          coordinatesRef.current = null;
        }
      }
    };
    t12 = [searchParams, updateHazardData];
    $[17] = searchParams;
    $[18] = updateHazardData;
    $[19] = t11;
    $[20] = t12;
  } else {
    t11 = $[19];
    t12 = $[20];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[21] !== liquefactionData || $[22] !== softStoryData || $[23] !== tsunamiData) {
    t13 = () => {
      const sources = [{
        name: "Soft Story Buildings",
        data: softStoryData
      }, {
        name: "Tsunami Zones",
        data: tsunamiData
      }, {
        name: "Liquefaction Zones",
        data: liquefactionData
      }];
      const errors = sources.filter(_temp).map(_temp2);
      if (errors.length > 0) {
        if (!toaster.isVisible("data-load-failed")) {
          toaster.create({
            id: "data-load-failed",
            title: "Data Load Error",
            description: errors.join(" | "),
            type: "error",
            duration: 5000,
            closable: true
          });
        }
      }
    };
    t14 = [softStoryData, tsunamiData, liquefactionData];
    $[21] = liquefactionData;
    $[22] = softStoryData;
    $[23] = tsunamiData;
    $[24] = t13;
    $[25] = t14;
  } else {
    t13 = $[24];
    t14 = $[25];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[26] !== currentView) {
    t15 = () => {
      if (currentView === "") {
        handleResize();
      }
      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    };
    t16 = [currentView];
    $[26] = currentView;
    $[27] = t15;
    $[28] = t16;
  } else {
    t15 = $[27];
    t16 = $[28];
  }
  useEffect(t15, t16);
  let t17;
  if ($[29] !== handleSearchChange || $[30] !== isSearchComplete || $[31] !== searchedAddress) {
    t17 = <HomeHeader searchedAddress={searchedAddress} isSearchComplete={isSearchComplete} onSearchChange={handleSearchChange} />;
    $[29] = handleSearchChange;
    $[30] = isSearchComplete;
    $[31] = searchedAddress;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      "--header-height": "198px",
      md: {
        "--header-height": "175px"
      },
      xl: {
        "--header-height": "141px"
      },
      "2xl": {
        "--header-height": "149px"
      },
      "--whitespace-height": "96px",
      "--map-height": "calc(100dvh - var(--header-height) - var(--whitespace-height))"
    };
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  let t20;
  let t21;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      base: "relative",
      sm: "static"
    };
    t20 = {
      base: "stretch",
      sm: "start"
    };
    t21 = {
      base: "block",
      sm: "flex"
    };
    $[34] = t19;
    $[35] = t20;
    $[36] = t21;
  } else {
    t19 = $[34];
    t20 = $[35];
    t21 = $[36];
  }
  let t22;
  if ($[37] !== addressHazardData || $[38] !== currentView || $[39] !== isHazardDataLoading || $[40] !== showHazards || $[41] !== toggledStates) {
    t22 = currentView === "desktop" ? <Box h="full" overflowY={{
      base: "visible",
      sm: "auto"
    }}><ReportHazards addressHazardData={addressHazardData} isHazardDataLoading={isHazardDataLoading} toggledStates={toggledStates} setToggledStates={setToggledStates} setLayerToggleObj={setLayerToggleObj} />{" "}</Box> : currentView === "mobile" ? <Box zIndex="docked" top="0" position="absolute"><MobileReportHazards showHazards={showHazards} addressHazardData={addressHazardData} isHazardDataLoading={isHazardDataLoading} toggledStates={toggledStates} setShowHazards={setShowHazards} setToggledStates={setToggledStates} setLayerToggleObj={setLayerToggleObj} /></Box> : null;
    $[37] = addressHazardData;
    $[38] = currentView;
    $[39] = isHazardDataLoading;
    $[40] = showHazards;
    $[41] = toggledStates;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      base: "initial",
      sm: "1"
    };
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  const t24 = coordinates || defaultCoords;
  let t25;
  if ($[44] !== layerToggleObj || $[45] !== liquefactionData || $[46] !== softStoryData || $[47] !== t24 || $[48] !== tsunamiData) {
    t25 = <Box flex={t23} h="full"><Map coordinates={t24} softStoryData={softStoryData} tsunamiData={tsunamiData} liquefactionData={liquefactionData} layerToggleObj={layerToggleObj} /></Box>;
    $[44] = layerToggleObj;
    $[45] = liquefactionData;
    $[46] = softStoryData;
    $[47] = t24;
    $[48] = tsunamiData;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== t22 || $[51] !== t25) {
    t26 = <Box w="full" css={t18} h="var(--map-height)" m="auto" position={t19} alignItems={t20} display={t21}>{t22}{t25}</Box>;
    $[50] = t22;
    $[51] = t25;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== t17 || $[54] !== t26) {
    t27 = <>{t17}{t26}</>;
    $[53] = t17;
    $[54] = t26;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  return t27;
};
export default AddressMapper;
function _temp(src) {
  return isErrorResult(src.data);
}
function _temp2(src_0) {
  return `${src_0.name}: ${(src_0.data as unknown as ErrorResult).message || "Unknown error"}`;
}